import React, { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, Pause, Play, SkipForward, Volume2 } from "lucide-react";

const AudioPlayer = ({
  audioSrc,
  autoStartIn = 10,
  audioRef: externalRef,
  onEnded,
  onDurationChange,
}) => {
  const internalRef = useRef(null);
  const audioRef = externalRef || internalRef;
  const countdownRef = useRef(null);
  const hasStartedOnceRef = useRef(false);
  const lastActionWasSkipOrAutoPlay = useRef(false);
  const onEndedRef = useRef(onEnded);
  const onDurationChangeRef = useRef(onDurationChange);
  const volumeRef = useRef(0.5);

  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [volume, setVolume] = useState(0.5);
  const [countdown, setCountdown] = useState(autoStartIn);
  const [countdownActive, setCountdownActive] = useState(false);
  const [loadingAudio, setLoadingAudio] = useState(true);

  useEffect(() => {
    onEndedRef.current = onEnded;
  }, [onEnded]);

  useEffect(() => {
    onDurationChangeRef.current = onDurationChange;
  }, [onDurationChange]);

  useEffect(() => {
    volumeRef.current = volume;
  }, [volume]);

  const playBeep = useCallback(() => {
    if (!lastActionWasSkipOrAutoPlay.current) return;

    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    osc.frequency.value = 1000;
    osc.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.2);
  }, []);

  const startAudio = useCallback(
    (isAutoOrSkip = false) => {
      const audio = audioRef.current;
      if (!audio) return;

      lastActionWasSkipOrAutoPlay.current = isAutoOrSkip;
      playBeep();

      audio
        .play()
        .then(() => {
          setIsPlaying(true);
          setCountdownActive(false);
          hasStartedOnceRef.current = true;
        })
        .catch(() => {});
    },
    [audioRef, playBeep]
  );

  const startCountdown = useCallback(() => {
    clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          startAudio(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [startAudio]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;
    let readyHandled = false;

    audio.pause();
    audio.currentTime = 0;
    audio.volume = volumeRef.current;
    audio.loop = false;

    setIsPlaying(false);
    setProgress(0);
    setCountdown(autoStartIn);
    setCountdownActive(false);
    setLoadingAudio(true);
    hasStartedOnceRef.current = false;
    clearInterval(countdownRef.current);

    const reportDuration = () => {
      if (Number.isFinite(audio.duration) && audio.duration > 0) {
        onDurationChangeRef.current?.(audio.duration);
      }
    };

    const handleReady = () => {
      if (readyHandled) return;
      readyHandled = true;
      setLoadingAudio(false);

      if (!hasStartedOnceRef.current) {
        setCountdownActive(true);
        startCountdown();
      }
    };

    const handleEnded = () => {
      clearInterval(countdownRef.current);
      setIsPlaying(false);
      setProgress(100);
      setCountdownActive(false);
      onEndedRef.current?.();
    };

    audio.addEventListener("loadedmetadata", reportDuration);
    audio.addEventListener("canplaythrough", handleReady);
    audio.addEventListener("ended", handleEnded);

    if (audio.readyState >= 1) reportDuration();
    if (audio.readyState >= 3) handleReady();

    return () => {
      audio.removeEventListener("loadedmetadata", reportDuration);
      audio.removeEventListener("canplaythrough", handleReady);
      audio.removeEventListener("ended", handleEnded);
      clearInterval(countdownRef.current);
    };
  }, [audioRef, audioSrc, autoStartIn, startCountdown]);

  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.volume = volume;
    }
  }, [audioRef, volume]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;

    const updateProgress = () => {
      if (audio.duration) {
        setProgress((audio.currentTime / audio.duration) * 100);
      }
    };

    audio.addEventListener("timeupdate", updateProgress);

    return () => audio.removeEventListener("timeupdate", updateProgress);
  }, [audioRef]);

  const skipCountdown = () => {
    clearInterval(countdownRef.current);
    setCountdown(0);
    setCountdownActive(false);
    startAudio(true);
  };

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;

    lastActionWasSkipOrAutoPlay.current = false;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio.play().then(() => setIsPlaying(true));
    }
  };

  const handleVolumeChange = (event) => {
    const nextVolume = Number(event.target.value);
    const audio = audioRef.current;

    if (audio) {
      audio.volume = nextVolume;
    }

    setVolume(nextVolume);
  };

  const controlsDisabled = countdownActive || loadingAudio;

  return (
    <div className="mx-auto w-full rounded-md bg-slate-50 p-4 ring-1 ring-slate-100">
      <audio ref={audioRef} src={audioSrc} preload="auto" />

      {loadingAudio && (
        <div className="flex min-h-[152px] items-center justify-center rounded-md bg-white text-sm font-semibold text-gray-500 shadow-sm">
          <Loader2 className="mr-2 h-4 w-4 animate-spin text-purple-700" />
          Loading audio
        </div>
      )}

      {!loadingAudio && countdownActive && !hasStartedOnceRef.current && (
        <div className="rounded-md bg-purple-50 p-4 text-center ring-1 ring-purple-100">
          <p className="text-sm font-semibold uppercase text-purple-700">
            Audio starts in
          </p>
          <p className="mt-1 text-3xl font-extrabold text-purple-900">
            {countdown}s
          </p>
          <button
            type="button"
            onClick={skipCountdown}
            className="mt-3 inline-flex min-h-10 items-center justify-center gap-2 rounded-md bg-violet-600 px-4 py-2 text-sm font-bold text-white shadow-sm transition hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2"
          >
            <SkipForward className="h-4 w-4" aria-hidden="true" />
            Skip
          </button>
        </div>
      )}

      {!loadingAudio && (
        <div className="mt-4 rounded-md bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <button
              type="button"
              onClick={togglePlay}
              disabled={controlsDisabled}
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md bg-violet-600 px-5 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300"
            >
              {isPlaying ? (
                <Pause className="h-4 w-4" aria-hidden="true" />
              ) : (
                <Play className="h-4 w-4" aria-hidden="true" />
              )}
              {isPlaying ? "Pause" : "Play"}
            </button>

            <label className="flex min-w-0 flex-1 items-center gap-3 text-sm font-semibold text-gray-500">
              <Volume2 className="h-4 w-4 shrink-0 text-purple-700" />
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={volume}
                disabled={controlsDisabled}
                onChange={handleVolumeChange}
                className="h-2 w-full accent-violet-600"
                aria-label="Volume"
              />
            </label>
          </div>

          <div className="mt-4 h-2 overflow-hidden rounded-full bg-purple-100">
            <div
              style={{ width: `${progress}%` }}
              className="h-full rounded-full bg-violet-600 transition-all"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AudioPlayer;
